<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';

  import { IconRadio, DatePicker } from '@/shared/ui';
  import NotificationDatePicker from './NotificationDatePicker.vue';
  import ValidityDatePicker from './ValidityDatePicker.vue';
  import { PositionUserSelector as PositionUserSelection } from '@/shared/ui';
  import { usePositionUserSelection } from '@/entities/User';
  import { isActiveUser, type ApiResponse, type NotificationMember, type UserItem } from '@/shared/lib';
  import type { Position } from '@/entities/Position';

  import { useDocumentValidity } from '../model/validity/useDocumentValidity';

  const props = defineProps<{
    hasValidity: string;
    validityDate: Date | null;
    notificationDates: Date[];
    validateFromToday?: boolean;
    notificationMembers?: NotificationMember[];
    id: number | undefined;
    positionsResponse?: ApiResponse<Position[]>;
  }>();

  const minSelectableDate = computed(() => {
    if (!props.validateFromToday) return undefined;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  const emit = defineEmits<{
    (e: 'update:has-validity', value: string): void;
    (e: 'update:validity-date', value: Date | null): void;
    (e: 'update:notification-dates', value: Date[]): void;
    (e: 'update:notified-users-count', value: number): void;
    (e: 'update:notified-member-ids', value: number[]): void;
  }>();

  const inactiveNotifiedMembers = computed<UserItem[]>(() =>
    (props.notificationMembers ?? [])
      .filter((member) => !isActiveUser(member.user))
      .map((member) => ({
        id: member.id,
        memberId: member.id,
        userId: member.user.id,
        name: member.user.name,
        positions: member.positions,
        membership: { id: member.id, positions: member.positions },
        status: member.user.status,
        deletedAt: member.user.deletedAt,
      }))
  );

  const {
    positions: fetchedPositions,
    users,
    selectedPositions,
    selectedUsers,
    positionsSelectedCount,
    usersSelectedCount,
    fetchPositionsAndUsers,
    initializeSelections,
  } = usePositionUserSelection({
    mode: 'by-selected-positions',
    getUserId: (user) => user.membership?.id ?? user.memberships?.[0]?.id ?? user.id,
    extraMembers: inactiveNotifiedMembers,
  });

  const positions = computed(() => props.positionsResponse ?? fetchedPositions.value);

  const {
    hasValidity,
    validityDate,
    notificationDates,
    isNotificationDatePickerOpen,
    isValidityDatePickerOpen,
    openValidityDatePicker,
    updateNotificationDates,
    openNotificationDatePicker,
    onNotificationDateSelected,
    onValidityDateSelected,
    selectedDateForNotification,
    selectedDateForValidity,
  } = useDocumentValidity(props);

  const getSelectedPositionIdsFromMembers = (members: NotificationMember[]) =>
    [
      ...new Set(
        members.flatMap((member) => (member.positions ?? []).map((position) => String(position.id)))
      ),
    ];

  const getSelectedMemberIdsFromMembers = (members: NotificationMember[]) =>
    [...new Set(members.map((member) => String(member.id)))];

  const hydrateSelectionFromMembers = async (members: NotificationMember[]) => {
    const positionIds = getSelectedPositionIdsFromMembers(members);
    if (positionIds.length === 0) {
      selectedPositions.value = [];
      selectedUsers.value = [];
      return;
    }
    await initializeSelections(positionIds, () => getSelectedMemberIdsFromMembers(members));
  };

  const lastHydratedMembersKey = ref('');

  const syncHydratedMembers = async (members: NotificationMember[]) => {
    const membersKey = `${props.id}:${members.map((m) => m.id).join(',')}`;
    if (membersKey === lastHydratedMembersKey.value) return;
    lastHydratedMembersKey.value = membersKey;
    await hydrateSelectionFromMembers(members);
  };

  watch([() => props.id, () => props.notificationMembers], ([, members]) => {
    syncHydratedMembers(members ?? []);
  });

  watch(hasValidity, (newVal) => {
    emit('update:has-validity', newVal);
  });

  watch(validityDate, (newVal) => {
    emit('update:validity-date', newVal);
  });

  watch(notificationDates, (newVal) => {
    emit('update:notification-dates', newVal);
  });

  watch(usersSelectedCount, (count) => {
    emit('update:notified-users-count', count);
  });

  watch(selectedUsers, (users) => {
    emit('update:notified-member-ids', users.map(Number));
  });

  onMounted(async () => {
    await fetchPositionsAndUsers();
    await syncHydratedMembers(props.notificationMembers ?? []);
  });
</script>

<template>
  <div class="flex flex-col gap-3 p-6">
    <label class="text-sm font-semibold text-gray-07">Documento tem validade?</label>
    <div class="flex gap-6">
      <IconRadio
        v-model="hasValidity"
        value="true"
        label="Sim"
        name="hasValidity"
      />
      <IconRadio
        v-model="hasValidity"
        value="false"
        label="Não"
        name="hasValidity"
      />
    </div>
  </div>
  <ValidityDatePicker
    v-if="hasValidity === 'true'"
    :validity-date="validityDate"
    @update:validity-date="openValidityDatePicker"
  />
  <NotificationDatePicker
    v-if="hasValidity === 'true'"
    :notification-dates="notificationDates"
    @update:notification-dates="updateNotificationDates"
    @open-date-picker="openNotificationDatePicker"
  />
  <div
    v-if="hasValidity === 'true'"
    class="flex flex-col gap-3 p-6"
  >
    <label class="text-sm font-semibold text-gray-07">
      Selecione os colaboradores notificados:
    </label>

    <PositionUserSelection
      user-selection-id-type="memberId"
      show-member-status
      :positions="positions"
      :users="users"
      :selected-positions="selectedPositions"
      :selected-users="selectedUsers"
      :positions-selected-count="positionsSelectedCount"
      :users-selected-count="usersSelectedCount"
      @update:selected-positions="selectedPositions = $event"
      @update:selected-users="selectedUsers = $event"
    />
  </div>

  <DatePicker
    v-model:selected-date="selectedDateForNotification"
    v-model:is-open="isNotificationDatePickerOpen"
    :multiple="false"
    :disable-past-dates="props.validateFromToday"
    :min-date="minSelectableDate"
    @update:selected-date="onNotificationDateSelected"
  />

  <DatePicker
    v-model:selected-date="selectedDateForValidity"
    v-model:is-open="isValidityDatePickerOpen"
    :multiple="false"
    :disable-past-dates="props.validateFromToday"
    :min-date="minSelectableDate"
    @update:selected-date="onValidityDateSelected"
  />
</template>
